import api from "@/lib/api";
import { toast } from "sonner";
import { isImageDataUrl, ocrImageInBrowser } from "@/lib/localBloodTestOcr";

const activePolls = new Map();

export function stopBloodTestPolling(reportId) {
  const timer = activePolls.get(reportId);
  if (timer) {
    clearInterval(timer);
    activePolls.delete(reportId);
  }
}

export function stopAllBloodTestPolling() {
  for (const [id] of activePolls) {
    stopBloodTestPolling(id);
  }
}

function showBloodTestError(data, fallback) {
  toast.error(data?.ai_error || fallback);
}

async function serverExtract(reportId, useAiOcr = false) {
  const { data } = await api.post(
    `/blood-tests/${reportId}/extract`,
    { use_ai_ocr: useAiOcr },
    { timeout: 90000 }
  );
  return data;
}

async function submitExtractedText(reportId, text, source = "manual") {
  const { data } = await api.post(
    `/blood-tests/${reportId}/set-text`,
    { text, source },
    { timeout: 30000 }
  );
  return data;
}

/**
 * Step 1: extract text without Mistral (local PDF / server Tesseract).
 * Optional: browser OCR or manual paste on failure.
 */
export async function extractBloodTestText(reportId, {
  onUpdate,
  fileDataUrl = null,
  useAiOcrFallback = false,
  onNeedManualText = null,
} = {}) {
  onUpdate?.({ ai_status: "processing" });

  let extracted = await serverExtract(reportId, false);
  onUpdate?.(extracted);

  if (extracted.ai_status !== "error") {
    return extracted;
  }

  // Browser OCR for images (no Mistral)
  if (fileDataUrl && isImageDataUrl(fileDataUrl)) {
    try {
      toast.info("OCR navigateur en cours…");
      const text = await ocrImageInBrowser(fileDataUrl);
      if (text.length >= 20) {
        extracted = await submitExtractedText(reportId, text, "client_ocr");
        onUpdate?.(extracted);
        toast.success("Texte extrait via OCR navigateur");
        return extracted;
      }
    } catch {
      // continue to fallbacks
    }
  }

  // Optional Mistral OCR fallback
  if (useAiOcrFallback) {
    toast.info("Tentative OCR IA (Mistral)…");
    extracted = await serverExtract(reportId, true);
    onUpdate?.(extracted);
    if (extracted.ai_status !== "error") {
      return extracted;
    }
  }

  if (onNeedManualText) {
    onNeedManualText(extracted);
    return { ...extracted, needs_manual_text: true };
  }

  showBloodTestError(extracted, "Impossible d'extraire le texte automatiquement");
  return extracted;
}

/**
 * Steps 2–3: Mistral analyzes extracted TEXT only (not the image).
 */
export async function analyzeBloodTestText(reportId, { onUpdate } = {}) {
  const { data: parsed } = await api.post(`/blood-tests/${reportId}/parse-markers`, {}, { timeout: 90000 });
  onUpdate?.(parsed);
  if (parsed.ai_status === "error") {
    showBloodTestError(parsed, "Échec de l'extraction des biomarqueurs");
    return parsed;
  }

  const { data: final_ } = await api.post(`/blood-tests/${reportId}/interpret`, {}, { timeout: 90000 });
  onUpdate?.(final_);

  if (final_.ai_status === "done") {
    toast.success(`Analyse terminée — ${final_.marker_count || 0} biomarqueurs`);
  } else if (final_.ai_status === "error") {
    showBloodTestError(final_, "L'interprétation IA a échoué. Cliquez sur Réanalyser.");
  }

  return final_;
}

export async function runBloodTestAnalysis(reportId, options = {}) {
  try {
    const extracted = await extractBloodTestText(reportId, options);
    if (extracted.ai_status === "error" || extracted.needs_manual_text) {
      return extracted;
    }
    return await analyzeBloodTestText(reportId, options);
  } catch (err) {
    if (err.response?.status === 409) {
      pollBloodTestReport(reportId, options);
      return null;
    }
    toast.error("Erreur pendant l'analyse du bilan");
    options.onUpdate?.({ ai_status: "error" });
    return { ai_status: "error" };
  }
}

export async function triggerBloodTestAnalysis(reportId, callbacks = {}) {
  return runBloodTestAnalysis(reportId, callbacks);
}

export async function triggerBloodTestReanalysis(reportId, callbacks = {}) {
  try {
    const { data } = await api.post(`/blood-tests/${reportId}/reanalyze`, {}, { timeout: 30000 });
    callbacks.onUpdate?.(data);
  } catch {
    toast.error("Erreur lors de la réinitialisation");
    return;
  }
  return runBloodTestAnalysis(reportId, callbacks);
}

export async function submitManualBloodTestText(reportId, text, callbacks = {}) {
  const extracted = await submitExtractedText(reportId, text, "manual");
  callbacks.onUpdate?.(extracted);
  if (extracted.ai_status !== "extracted") {
    return extracted;
  }
  return analyzeBloodTestText(reportId, callbacks);
}

export function pollBloodTestReport(reportId, { onUpdate, onDone } = {}) {
  stopBloodTestPolling(reportId);

  const tick = async () => {
    try {
      const { data } = await api.get(`/blood-tests/${reportId}`);
      onUpdate?.(data);

      if (data.ai_status === "done") {
        stopBloodTestPolling(reportId);
        onDone?.(data);
      } else if (data.ai_status === "error") {
        stopBloodTestPolling(reportId);
        onDone?.(data);
      }
    } catch {
      // ignore transient network errors while polling
    }
  };

  tick();
  const timer = setInterval(tick, 3000);
  activePolls.set(reportId, timer);
}